import {
  faCircleInfo,
  faFilePdf,
  faPen,
  faPlus,
  faStop,
  faTrashCan,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AutoGrowTextarea } from "../../chat/AutoGrowTextarea";
import { markdownComponents } from "../../chat/Markdown";
import { useWorkspaceKavel } from "../kavel";
import { useWorkspaceSearch } from "../search";
import { activeParcelFor, chipLabel, chipParcel } from "./activeParcel";
import { filterButtonTitle, filterCountLabel } from "./filterCount";
import { useWorkspace } from "./store";
import {
  FilterSuggestion,
  ReportProposal,
  ResearchProposal,
  WorkspaceMessage,
  pdfPending,
} from "./types";

// Wat de rapportknop in de sessiebalk verstuurt; het model antwoordt met een
// <report>-voorstel, en daarna loopt alles langs dezelfde weg als een getypte vraag.
export const REPORT_REQUEST = "Maak een rapport van dit gesprek.";

const dateLabel = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) : "";

const FilterChip = ({ filter, onApply }: { filter: FilterSuggestion; onApply?: () => void }) => {
  const activeId = useWorkspaceSearch((s) => s.filterId);
  const apply = useWorkspaceSearch((s) => s.apply);
  const active = activeId === filter.id;
  const count = filterCountLabel(filter.total, filter.capped);
  // Zero hits is a measured outcome: the button should then do nothing.
  const empty = filter.total === 0;

  return (
    <button
      onClick={() => {
        apply(filter.id, filter.criteria, filter.label, filter.total ?? undefined);
        onApply?.();
      }}
      aria-pressed={active}
      disabled={empty}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${
        empty
          ? "cursor-default border-gray-200 bg-gray-50 text-gray-400"
          : active
            ? "border-dark-green bg-yellow/30"
            : "border-yellow bg-bright-yellow hover:bg-yellow"
      }`}
      title={filterButtonTitle(filter.total, filter.capped)}
    >
      <span className={`italic ${empty ? "" : "text-black"}`}>{filter.label || "Filter"}</span>
      {count && <span className={empty ? "" : "text-dark-green"}>{count}</span>}
    </button>
  );
};

const timeLabel = (iso?: string) =>
  iso ? new Date(iso).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }) : "";

// Drie stipjes terwijl het eerste stukje antwoord onderweg is.
const Typing = () => (
  <span className="flex gap-1 py-1">
    {[0, 150, 300].map((delay) => (
      <span
        key={delay}
        className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400"
        style={{ animationDelay: `${delay}ms` }}
      />
    ))}
  </span>
);

const ReportProposalCard = ({ proposal }: { proposal: ReportProposal }) => {
  const createReportPdf = useWorkspace((s) => s.createReportPdf);
  // Alleen het verzoek zelf kan hier mislukken; het maken meldt zich via pdfStatus.
  const [requestFailed, setRequestFailed] = useState(false);
  const pending = pdfPending(proposal.pdfStatus);
  const make = async () => {
    setRequestFailed(false);
    const ok = await createReportPdf(proposal.id);
    setRequestFailed(!ok);
  };
  const failure = requestFailed
    ? "Aanvragen mislukt; probeer het opnieuw."
    : proposal.pdfStatus === "failed"
      ? proposal.pdfError || "Maken mislukt; probeer het opnieuw."
      : null;
  return (
    <div className="mt-1.5 max-w-[85%] rounded-lg border border-yellow bg-yellow/10 p-2 text-xs">
      <p className="font-bold">
        <FontAwesomeIcon icon={faFilePdf} size="sm" className="mr-1" />
        Rapport: {proposal.title}
      </p>
      {proposal.situation && (
        <div className="mt-1 text-gray-700">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {proposal.situation}
          </ReactMarkdown>
        </div>
      )}
      {proposal.conclusion && (
        <div className="mt-1 border-t border-yellow/60 pt-1 text-gray-700">
          <p className="font-bold">Conclusie</p>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {proposal.conclusion}
          </ReactMarkdown>
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {proposal.fileUrl ? (
          <a
            href={proposal.fileUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded bg-bright-yellow px-2.5 py-1 font-bold hover:bg-yellow"
          >
            Open PDF
          </a>
        ) : null}
        <button
          type="button"
          onClick={make}
          disabled={pending}
          className="rounded border border-yellow px-2.5 py-1 font-bold hover:bg-yellow/40 disabled:opacity-50"
        >
          {pending ? "PDF wordt gemaakt…" : proposal.fileUrl ? "Opnieuw maken" : "Maak PDF"}
        </button>
        {failure && <span className="text-red-600">{failure}</span>}
      </div>
    </div>
  );
};

const Message = ({
  message,
  onApplyFilter,
  showFilterTip,
  onResearchProposal,
}: {
  message: WorkspaceMessage;
  onApplyFilter?: () => void;
  showFilterTip?: boolean;
  onResearchProposal?: (proposal: ResearchProposal) => void;
}) => {
  const mine = message.role === "user";
  // Een antwoord dat alleen uit een voorstel bestond, heeft geen tekst; dan geen
  // lege bel met typ-indicator, alleen de voorstelkaart eronder.
  const proposalOnly =
    !mine &&
    !message.content &&
    ((message.reportProposals?.length ?? 0) > 0 || (message.researchProposals?.length ?? 0) > 0);
  return (
    <div className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
      {!proposalOnly && (
        <div
          className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs wrap-break-word ${
            mine
              ? "rounded-br-sm bg-light-green text-black"
              : "rounded-bl-sm border border-gray-200 bg-white text-black"
          }`}
        >
          {mine ? (
            <p className="whitespace-pre-wrap">{message.content}</p>
          ) : message.content ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {message.content}
            </ReactMarkdown>
          ) : (
            <Typing />
          )}
          {!message.finished && <p className="pt-1 text-[11px] italic text-gray-500">afgebroken</p>}
        </div>
      )}

      {message.filters.length > 0 && (
        <div className="max-w-[85%] pt-1.5">
          {/* The icon sits beside the chips, not among them: a long filter label
              fills the whole row, and wrapping would drop it onto its own line. */}
          <div className="flex items-start gap-1.5">
            <div className="flex min-w-0 flex-wrap gap-1.5">
              {message.filters.map((filter) => (
                <FilterChip key={filter.id} filter={filter} onApply={onApplyFilter} />
              ))}
            </div>
            {!showFilterTip && (
              <span
                tabIndex={0}
                role="img"
                aria-label="Uitleg over de filterknop"
                title="Klik op de gele filterknop om de gevonden percelen op de kaart te tonen."
                className="inline-flex shrink-0 items-center pt-1.5 text-gray-400 hover:text-gray-600 focus:text-gray-600 focus:outline-none"
              >
                <FontAwesomeIcon icon={faCircleInfo} size="sm" />
              </span>
            )}
          </div>
          {showFilterTip && (
            <p className="pt-1 text-[11px] leading-snug text-gray-500">
              Tip: klik op de gele filterknop om de gevonden percelen op de kaart te tonen.
            </p>
          )}
        </div>
      )}
      {message.researchProposals?.map((proposal) => (
        <div
          key={proposal.id}
          className="mt-1.5 max-w-[85%] rounded-lg border border-yellow bg-yellow/10 p-2 text-xs"
        >
          <p className="font-bold">Onderzoek {proposal.parcelName}</p>
          {proposal.extraQuestion && (
            <>
              <p className="mt-0.5 text-gray-600">{proposal.extraQuestion}</p>
              <p className="mt-0.5 text-[11px] text-gray-500">
                Alleen deze vraag; de standaardonderwerpen blijven buiten beschouwing.
              </p>
            </>
          )}
          <button
            type="button"
            onClick={() => onResearchProposal?.(proposal)}
            className="mt-2 rounded bg-bright-yellow px-2.5 py-1 font-bold hover:bg-yellow"
          >
            Onderzoek starten
          </button>
        </div>
      ))}
      {message.reportProposals?.map((proposal) => (
        <ReportProposalCard key={proposal.id} proposal={proposal} />
      ))}
      {message.created && (
        <span className="px-1 pt-0.5 text-[10px] text-gray-400">{timeLabel(message.created)}</span>
      )}
    </div>
  );
};

const SessionHeader = ({
  onSelect,
  onReport,
}: {
  onSelect: (sessionId?: number) => void;
  onReport: () => void;
}) => {
  const { sessions, activeSessionId, renameSession, deleteSession, isStreaming } = useWorkspace();
  const active = useMemo(
    () => sessions.find((s) => s.id === activeSessionId),
    [sessions, activeSessionId]
  );

  return (
    <div className="flex items-center gap-1 border-b px-2 py-1.5">
      <select
        value={activeSessionId ?? ""}
        onChange={(e) => onSelect(e.target.value ? Number(e.target.value) : undefined)}
        className="flex-1 min-w-0 truncate border border-gray-200 rounded px-2 py-1 text-xs"
      >
        {!activeSessionId && <option value="">Nieuwe chat</option>}
        {sessions.map((session) => (
          <option key={session.id} value={session.id}>
            {session.title || "Zonder titel"} · {dateLabel(session.modified)}
          </option>
        ))}
      </select>
      <button
        onClick={() => onSelect(undefined)}
        title="Nieuwe chat"
        className="px-2 py-1 text-gray-500 hover:text-black"
      >
        <FontAwesomeIcon icon={faPlus} size="sm" />
      </button>
      {active && (
        <>
          <button
            onClick={onReport}
            disabled={isStreaming}
            title="Rapport van dit gesprek (PDF)"
            className="px-2 py-1 text-gray-500 hover:text-black disabled:opacity-40"
          >
            <FontAwesomeIcon icon={faFilePdf} size="sm" />
          </button>
          <button
            onClick={() => {
              const title = prompt("Nieuwe titel", active.title);
              if (title?.trim()) renameSession(active.id, title.trim());
            }}
            title="Hernoemen"
            className="px-2 py-1 text-gray-500 hover:text-black"
          >
            <FontAwesomeIcon icon={faPen} size="sm" />
          </button>
          <button
            onClick={() => {
              if (confirm("Dit gesprek verwijderen?")) {
                deleteSession(active.id);
                onSelect(undefined);
              }
            }}
            title="Verwijderen"
            className="px-2 py-1 text-gray-300 hover:text-red-500"
          >
            <FontAwesomeIcon icon={faTrashCan} size="sm" />
          </button>
        </>
      )}
    </div>
  );
};

export const ChatPanel = ({
  parcels,
  onSelectSession,
  onApplyFilter,
  onResearchProposal,
}: {
  parcels?: { id: number; name: string }[];
  onSelectSession: (id?: number) => void;
  onApplyFilter?: () => void;
  onResearchProposal?: (proposal: ResearchProposal) => void;
}) => {
  const { messages, isStreaming, error, sendMessage, stop } = useWorkspace();
  const [input, setInput] = useState("");
  const clickedName = useWorkspaceKavel((state) => state.cube?.name);
  const [dismissed, setDismissed] = useState<string>();
  // Een nieuwe selectie brengt een weggeklikte chip terug.
  useEffect(() => setDismissed(undefined), [clickedName]);
  const chip = chipParcel(activeParcelFor(clickedName, parcels), dismissed);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const firstFilterMessageIndex = messages.findIndex((message) => message.filters.length > 0);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isStreaming]);

  // Cursor staat klaar bij openen, en weer na een antwoord — zodat doorvragen
  // geen extra klik kost.
  useEffect(() => {
    if (!isStreaming) inputRef.current?.focus();
  }, [isStreaming]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    setInput("");
    sendMessage(trimmed, chip);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <SessionHeader
        onSelect={onSelectSession}
        onReport={() => {
          if (!isStreaming) sendMessage(REPORT_REQUEST);
        }}
      />
      <div className="flex-1 min-h-0 space-y-2 overflow-y-auto bg-gray-50 p-3">
        {messages.length === 0 ? (
          <p
            className="text-xs text-gray-400 cursor-text"
            onClick={() => inputRef.current?.focus()}
          >
            Stel een vraag over je percelen, of beschrijf wat voor kavels je zoekt — de assistent
            stelt dan een filter voor.
          </p>
        ) : (
          messages.map((message, idx) => (
            <Message
              // Een bericht dat nog streamt heeft nog geen id; berichten worden
              // alleen achteraan toegevoegd, dus de index is dan stabiel
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={message.id ?? idx}
              message={message}
              onApplyFilter={onApplyFilter}
              showFilterTip={idx === firstFilterMessageIndex}
              onResearchProposal={onResearchProposal}
            />
          ))
        )}
        <div ref={endRef} />
      </div>
      {error && <p className="px-2 pb-1 text-xs text-red-600">{error}</p>}
      {chip && (
        <div className="border-t bg-white px-2 pt-1.5 text-[11px]">
          <span className="inline-flex items-center gap-1 rounded-full bg-light-green px-2 py-0.5">
            Over: {chipLabel(chip)}
            <button
              type="button"
              onClick={() => setDismissed(chip.name)}
              className="text-gray-500 hover:text-black"
              title="Niet meesturen met je vraag"
              aria-label="Niet meesturen met je vraag"
            >
              <FontAwesomeIcon icon={faXmark} size="xs" />
            </button>
          </span>
        </div>
      )}
      <form
        onSubmit={onSubmit}
        className={`flex items-end gap-1.5 bg-white p-2 ${chip ? "" : "border-t"}`}
      >
        <AutoGrowTextarea
          ref={inputRef}
          className="flex-1 rounded-2xl border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs focus:bg-white"
          placeholder="Typ je vraag..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={isStreaming}
        />
        {isStreaming ? (
          <button
            type="button"
            onClick={stop}
            className="rounded-full bg-dark-green px-3 py-1.5 text-xs font-bold text-white"
            title="Stoppen"
          >
            <FontAwesomeIcon icon={faStop} size="sm" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={!input.trim()}
            className="rounded-full bg-bright-yellow px-3 py-1.5 text-xs font-bold hover:bg-yellow disabled:opacity-40"
          >
            Stuur
          </button>
        )}
      </form>
    </div>
  );
};
